import React from 'react';
import {useSelector} from 'react-redux';
import type {TaskDefinitionSimpleResponse} from 'src/api/ProactApiClient';
import {TaskTypeEnum} from 'src/api/ProactApiClient';
import {useGetTaskDefinitionListByRequestQuery} from 'src/redux/queries/taskDefinition';
import {CheckboxList} from 'src/design/atoms/CheckboxList';
import {TasksDataListMeta, taskRowSubText} from 'src/app/tasks/taskHelpers';
import {DataHeader} from 'src/design/organisms/DataHeader';
import {DataList} from 'src/design/organisms/DataList';
import {useListState} from 'src/design/organisms/useListState';
import type {RootState} from 'src/redux/store';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useDebounce from 'src/shared/hooks/useDebounce';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {ActionPillConfig, ActionsDocumentTitle} from '../actionHelpers';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const maxPageRows = 5;

const statuses = [
  {value: '1', label: 'Draft'},
  {value: '2', label: 'Published'},
  {value: '3', label: 'Completed'},
];

export const ListCurrentUserActionDefinitions: React.FC = () => {
  const {session} = useSelector((state: RootState) => state.session);
  const user = session?.user;
  useSetDocumentTitle(ActionsDocumentTitle);
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();
  const {
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    currentPage,
    handlePageChange,
    start,
    length,
  } = useListState({
    identifier: 'my-created-actions',
    pageSize: maxPageRows,
    defaultFilters: {states: ['1']},
  });
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const {data, isLoading, error} = useGetTaskDefinitionListByRequestQuery(
    {
      taskType: TaskTypeEnum.Actions,
      createdByUserId: user!.id,
      states: filters.states.map((x: string) => +x),
      sortColumn: 'id',
      sortDirection: 'desc',
      start,
      length,
      search: debouncedSearchTerm,
      includeDemonstrationGenerated: false,
    },
    {skip: !user}
  );

  React.useEffect(() => {
    if (error) {
      setAlert((error as any)?.message ?? 'Failed to load actions', 'error');
    }
  }, [error, setAlert]);

  const indexListData = data?.data;
  const totalRecords = data?.totalRecords ?? 0;

  const handleNavigateToTask = (id: string | number) =>
    navigate(PathUtility.ActionViewUser(id, 'content'));

  const handleSearchTerm = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {value} = e.target;
    setSearchTerm(value);
  };

  const handleStatusFilter = (statusValues: string[]) => {
    setFilters(prevState => ({...prevState, states: statusValues}));
  };

  return (
    <>
      <DataHeader
        titleText={'My Created Actions'}
        titleSize="sm"
        description="Actions created by you. They may be assigned to yourself or other people."
        actions={[]}
        filters={[
          <CheckboxList
            label="Statuses"
            values={statuses}
            selected={filters.states}
            onChange={handleStatusFilter}
          />,
        ]}
        searchTerm={searchTerm}
        onSearchTermChange={handleSearchTerm}
        isFilterActive={false}
      />
      <DataList
        isLoading={isLoading}
        data={indexListData}
        meta={TasksDataListMeta}
        currentPage={currentPage}
        totalRecords={totalRecords}
        recordsPerPage={maxPageRows}
        onRowClick={handleNavigateToTask}
        onPageChange={handlePageChange}
        onGetPillMapping={(item: TaskDefinitionSimpleResponse) =>
          ActionPillConfig(item.state, undefined, undefined, item.dueAt)
        }
        onRowSubTextRender={taskRowSubText}
        hasSearchTerm={searchTerm.trim().length > 0}
        hasCreationLogic={false}
      />
    </>
  );
};

export default ListCurrentUserActionDefinitions;
